import {
  createAction,
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  props as actionProps
} from '@ngrx/store';
import {HasChildren, Segment, SegmentState} from './segment.model';
import {SegmentSearchFilterModel} from './list/search/search-filter.model';
import {TargetAccount} from '../target/target.model';
import {targetsFeatureSelector} from '../target/target.state';
import {ErrorModel} from '../app.model';
import {ApiVersion} from '../app.types';
import {Page} from '../components/common-interfaces';

export interface SegmentWithAccount {
  segment: Segment;
  accounts: TargetAccount[];
}

export interface SegmentsListFilter {
  segmentName?: string,
  segmentIds?: string[],
  clientSideId?: string,
  ttdCompliance?: string;
  segmentMainTypes?: string[],
  segmentSubTypes?: string[],
  segmentAudienceTypes?: string[],
  distributionAccountIds?: number[],
  category?: string[],
  partnerId?: number,
  state?: SegmentState,
  applyFilterToChildren?: boolean,
  taxonomyIds?: number[],
  withAccounts: boolean,
  withPlatformAttributes: boolean
}

export interface SegmentsListFilterPage {
  page: number,
  size: number,
  sort: SegmentsListFilterSort
}

export interface SegmentsListFilterSort {
  sortField: string,
  direction: string
}

export interface SegmentsState {
  segments: Page<Segment>,
  segmentsIndex: { [id: string]: Segment },
  segmentDetails?: SegmentWithAccount,
  taxonomySegments: { [taxonomyId: string]: Segment[] },
  segmentsForTaxonomyAttachment: Segment[]
}

export const initialState: Readonly<SegmentsState> = {
  segments: {content: [], totalElements: 0},
  segmentsIndex: {},
  segmentDetails: undefined,
  taxonomySegments: {},
  segmentsForTaxonomyAttachment: []
};

// selectors
export const segmentsFeatureSelector = createFeatureSelector<SegmentsState>('segmentsSlice');
export const selectSegmentById = (id: string) => createSelector(
  segmentsFeatureSelector,
  (state) => state.segmentsIndex[id]
);
export const selectSegments = createSelector(
  segmentsFeatureSelector,
  (state) => state.segments
);
export const selectTaxonomySegments = (id: string) => createSelector(
  segmentsFeatureSelector,
  (state) => state.taxonomySegments[id]
);
export const selectSegmentsForTaxonomyAttachment = createSelector(
  segmentsFeatureSelector,
  (state) => state.segmentsForTaxonomyAttachment
);

export const selectSegmentDetails = createSelector(
  segmentsFeatureSelector,
  (state) => state.segmentDetails
);
export const selectSegmentDetailsWithTargets = createSelector(
  segmentsFeatureSelector,
  targetsFeatureSelector,
  (segments, targets) => ({details: segments.segmentDetails, allTargets: targets.targets})
);

// actions
export const loadSegments = createAction(
  '[Segments] Load segments',
  actionProps<{ filter: SegmentsListFilter, page: SegmentsListFilterPage, apiVersion: ApiVersion }>()
);
export const loadSegmentsForTaxonomy = createAction(
  '[Segments] Load segments for taxonomy',
  actionProps<{ filter: SegmentsListFilter, taxonomyId: number }>()
);
export const segmentsForTaxonomyLoaded = createAction(
  '[Segments] Segments for taxonomy loaded',
  actionProps<{ segments: Segment[], taxonomyId: number }>()
);
export const attachSegmentsToTaxonomy = createAction(
  '[Segments] Attach segments to taxonomy',
  actionProps<{ segmentIds: number[], taxonomyId: number }>()
);
export const segmentsAttached = createAction(
  '[Segments] Segments attached to taxonomy'
);
export const loadAllSegmentsForTaxonomyAttachment = createAction(
  '[Segments] Load all segments for taxonomy attachment',
  actionProps<{ filter: SegmentsListFilter }>()
);
export const allSegmentsForTaxonomyAttachmentLoaded = createAction(
  '[Segments] Segments for taxonomy attachment loaded',
  actionProps<{ segments: Segment[] }>()
);
export const reloadSegmentList = createAction(
  '[Segments] Reload segments list'
);
export const reloadSegmentListWithProps = createAction(
  '[Segments] Reload segments list with props',
  actionProps<{ props?: any }>()
);
export const multishareComplete = createAction(
  '[Share] Segments multishare complete',
  actionProps<{ segmentIds: number[] }>()
);
export const segmentsLoaded = createAction(
  '[Segments] Loaded segments',
  actionProps<{ segments: Page<Segment> }>()
);
export const loadSegmentsForExportPreview = createAction(
  '[Segments] Load segments for export preview',
  actionProps<{ filter: SegmentsListFilter, filterSearchValues: SegmentSearchFilterModel, apiVersion: ApiVersion }>()
);
export const loadSegmentsForExport = createAction(
  '[Segments] Load segments for export',
  actionProps<{ filter: SegmentsListFilter, filterSearchValues: SegmentSearchFilterModel, apiVersion: ApiVersion }>()
);
export const segmentsForExportPreviewLoaded = createAction(
  '[Segments] Loaded segments for export preview',
  actionProps<{ segments: Page<Segment>, filterSearchValues: SegmentSearchFilterModel, apiVersion: ApiVersion }>()
);
export const segmentsForExportLoaded = createAction(
  '[Segments] Loaded segments for export',
  actionProps<{ segments: Page<Segment>, filterSearchValues: SegmentSearchFilterModel }>()
);
export const loadParentSegments = createAction(
  '[Segments] Load parent segments candidates',
  actionProps<{ filter: SegmentsListFilter }>()
);
export const parentSegmentsLoaded = createAction(
  '[Segments] Loaded parent segments candidates',
  actionProps<{ segments: Page<Segment> }>()
);
export const selectionChanged = createAction(
  '[Segments] Segments selection changed',
  actionProps<{ selectedIds: number[] }>()
);
export const loadSegmentDetails = createAction(
  '[Segments] Open segment details sidenav for segmentId',
  actionProps<{ segmentId: number }>()
);
export const segmentDetailsLoaded = createAction(
  '[Segments] Segment details loaded',
  actionProps<{ segmentDetails: SegmentWithAccount }>()
);
export const closeSegmentDetails = createAction(
  '[Segments] Close segment details sidenav'
);
export const closeSegmentDetailsAndClearSelection = createAction(
  '[Segments] Close segment details and clear selection'
);
export const deleteSegment = createAction(
  '[Segments] Delete segment',
  actionProps<{ segment: Segment }>()
);
export const segmentDeleted = createAction(
  '[Segments] Deleted segment',
  actionProps<{ segment: Segment }>()
);
export const segmentDeletionError = createAction(
  '[Segments] Segment deletion error',
  actionProps<{ error: ErrorModel }>()
);
export const editSegment = createAction(
  '[Segments] Edit segment',
  actionProps<{ segment: Segment, makeGlobal: boolean, apiVersion: ApiVersion }>()
);
export const segmentEdited = createAction(
  '[Segments] Edited segment',
  actionProps<{ segment: Segment }>()
);
export const segmentEditError = createAction(
  '[Segments] Segment edit error',
  actionProps<{ error: ErrorModel }>()
);
export const segmentCreated = createAction(
  '[Segments] Created segment',
  actionProps<{ segment: Segment }>()
);
export const segmentCreationError = createAction(
  '[Segments] Segment creation error',
  actionProps<{ error: ErrorModel }>()
);


// @ts-ignore
export const flatten = <T>(segs?: HasChildren[]) => (segs || []).reduce((acc: HasChildren[], current: HasChildren) => {
  return [...acc, current, ...flatten(current.children)];
}, <HasChildren<T>[]>[]);

export const flattenToSegments = (segs?: Segment[]) => flatten(segs)
  .filter((it: Segment) => it.entityType === 'SEGMENT' || !it.entityType);
export const flattenToTaxonomies = (segs?: Segment[]) => flatten(segs)
  .filter((it: Segment) => it.entityType === 'TAXONOMY');

export const segmentsReducer = createReducer(
  initialState,
  on(segmentsLoaded, (state, {segments}) => {
    const getSegmentsIndex = (segs?: Segment[]) => (segs || []).reduce((accumulator, current) => {
      return {
        ...accumulator,
        [current.id]: current
      };
    }, {});
    const flatSegments = flattenToSegments(segments.content);
    const index = getSegmentsIndex(flatSegments);
    return {
      ...state,
      segments,
      segmentsIndex: {...index}
    };
  }),
  on(segmentDetailsLoaded, (state, {segmentDetails}) => {
    return {
      ...state,
      segmentDetails
    };
  }),
  on(closeSegmentDetails, (state) => {
    return {
      ...state,
      segmentDetails: undefined
    };
  }),
  on(allSegmentsForTaxonomyAttachmentLoaded, (state, {segments}) => {
    return <SegmentsState>{
      ...state,
      segmentsForTaxonomyAttachment: segments
    };
  }),
  on(segmentsForTaxonomyLoaded, (state, {segments, taxonomyId}) => {
    return <SegmentsState>{
      ...state,
      taxonomySegments: {
        ...state.taxonomySegments,
        [taxonomyId]: flattenToSegments(segments).filter((it: Segment) => it.taxonomyId === taxonomyId)
      }
    };
  })
);
